import React, { useEffect, useState, useMemo, useContext } from "react";
import { useTranslation } from "react-i18next";
import {
  Card,
  Page,
  Banner,
  Layout,
  ExceptionList,
  Stack,
  ProgressBar,
  PageActions,
  Scrollable,
  Text,
  Button,
  Modal,
} from "@shopify/polaris";
import LocationsImportShowSkeleton from "./SkeletonShow";
import { TitleBar } from "@shopify/app-bridge-react";
import { useNavigate } from "react-router-dom";
import { TickMinor, AlertMinor } from "@shopify/polaris-icons";
import ProgressIndicator from "../../../../../components/components/ProgressIndicator";
import { createConsumer } from "@rails/actioncable";
import {
  useGetCSVImport,
  useDestroyCSVImport,
} from "../../../../../hooks/queries/locationImports";
import { AppContext } from "../../../../../components/global/appContext";

export default function LocationsImportShow(props) {
  const { t } = useTranslation();
  const { shopDomain } = useContext(AppContext);
  const navigate = useNavigate();

  const { data, isError, isLoading, isSuccess } = useGetCSVImport();
  const [currentImport, setCurrentImport] = useState(data);
  const [showCancelConfirmation, setShowCancelConfirmation] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  
  useEffect(() => setCurrentImport(data), [data]);

  const destroyCSVImportMutation = useDestroyCSVImport();
  const alreadyCompleted = currentImport?.completed === true;

  useEffect(() => {
    if (alreadyCompleted) return;

    const consumer = createConsumer(`/cable?shopify_domain=${shopDomain}`);

    const subscription = consumer.subscriptions.create(
      {
        channel: "ImportStatusChannel",
      },
      { received: (data) => setCurrentImport(data) }
    );
    return () => consumer.subscriptions.remove(subscription);
  }, [alreadyCompleted]);

  const handleCancelConfirm = () => {
    setShowCancelConfirmation(false);
    setIsCancelling(true);
    
    destroyCSVImportMutation.mutate(
      {},
      {
        onSuccess: () => {
          navigate("/places/upload/new");
        },
        onError: () => {
          setIsCancelling(false);
        }
      }
    );
  };

  const logsToExceptionListItems = (logs) =>
    useMemo(
      () =>
        logs?.map(({ name, valid, error_message }) => {
          return {
            icon: valid ? TickMinor : AlertMinor,
            title: name,
            status: valid ? undefined : "critical",
            description: error_message,
          };
        }) || [],
      [logs]
    );

  const locationImportItems = logsToExceptionListItems(
    currentImport?.location_logs
  );

  const failedImportItems = logsToExceptionListItems(
    currentImport?.failed_imports
  );

  if (isError) return <p>{t("common.error")}</p>;
  if (!data && isSuccess) navigate(`/places/upload/new`);
  if (!currentImport) return <LocationsImportShowSkeleton />;

  const inProgressMarkup = (
    <>
      <Layout.Section fullWidth>
        <Banner title={t("import.show.youCanLeavePage")} status="info">
          <p>{t("import.show.backgroundMessage")}</p>
        </Banner>
      </Layout.Section>
      <Layout.Section fullWidth>
        <Card>
          <Card.Section>
            <ProgressIndicator
              title={t("import.show.importProgress")}
              used={currentImport?.imported || 0}
              of={currentImport?.total || 0}
              suffix={t("import.show.suffix")}
              usedText={t("import.show.imported")}
              color="highlight"
              // size="large"
            />
          </Card.Section>
          <Card.Section title={t("import.show.importLog")}>
            <ExceptionList items={locationImportItems} />
          </Card.Section>
          <Card.Section>
            <Button 
              destructive
              onClick={() => setShowCancelConfirmation(true)}
              disabled={isCancelling}
              loading={isCancelling}
            >
              {t("import.show.cancelImport")}
            </Button>
          </Card.Section>
        </Card>
      </Layout.Section>
      <Modal
        open={showCancelConfirmation}
        onClose={() => setShowCancelConfirmation(false)}
        title={t("import.show.cancelConfirmation.title")}
        primaryAction={{
          content: t("import.show.cancelConfirmation.yesCancel"),
          onAction: handleCancelConfirm,
          destructive: true
        }}
        secondaryActions={[
          {
            content: t("import.show.cancelConfirmation.noContinue"),
            onAction: () => setShowCancelConfirmation(false)
          },
        ]}
      >
        <Modal.Section>
          <p>{t("import.show.cancelConfirmation.message")}</p>
          <p>{t("import.show.cancelConfirmation.note")}</p>
        </Modal.Section>
      </Modal>
    </>
  );

  const completedPageActions = (
    <PageActions
      primaryAction={{
        content: t("import.show.uploadAnother"),
        onAction: () => {
          destroyCSVImportMutation.mutate({}, {
            onSuccess: () => navigate("/places/upload/new")
          });
        },
        loading: destroyCSVImportMutation.isLoading,
      }}
      secondaryActions={[
        {
          content: t("import.show.backToLocations"),
          onAction: () => {
            destroyCSVImportMutation.mutate({}, {
              onSuccess: () => navigate("/places/locations")
            });
          },
          loading: destroyCSVImportMutation.isLoading,
        },
      ]}
    />
  );

  const successMarkup = (
    <>
      <Layout.Section fullWidth>
        <Banner title={t("import.show.importSucceeded")} status="success"></Banner>
      </Layout.Section>
      <Layout.Section fullWidth>
        <Card title={t("import.show.report")}>
          <Card.Section>
            <p style={{ marginBottom: "var(--p-space-2)" }}>
              {currentImport.status_message}
            </p>
            <Stack alignment="center">
              <Text  variant="bodyMd" as="span" color="subdued">
                {currentImport.successful_import_count} of{" "}
                {currentImport.imported} {t("import.show.imported")} {t("import.messages.completed").toLowerCase()}
              </Text>
              <Stack.Item fill>
                <ProgressBar
                  size="small"
                  progress={
                    (currentImport.successful_import_count /
                      currentImport.imported) *
                    100
                  }
                />
              </Stack.Item>
            </Stack>
          </Card.Section>
          {currentImport.failed_import_count > 0 ? (
            <Card.Section flush>
              <Scrollable style={{ maxHeight: "400px" }}>
                <div style={{ padding: "var(--p-space-5)" }}>
                  <div
                    style={{ paddingBottom: "var(--p-space-5)", paddingTop: 0 }}
                  >
                    <Text variant="headingXs" as="h3">{t("import.show.failedLocations")} ({currentImport.failed_import_count})</Text>
                  </div>

                  <ExceptionList items={failedImportItems} />
                </div>
              </Scrollable>
            </Card.Section>
          ) : null}
        </Card>
      </Layout.Section>
      <Layout.Section fullWidth>{completedPageActions}</Layout.Section>
    </>
  );

  const failureMarkup = (
    <>
      <Layout.Section>
        <Banner title={t("import.show.importFailed")} status="critical"></Banner>
      </Layout.Section>
      <Layout.Section>
        <Card title={t("import.show.report")}>
          <Card.Section>
            <p>{currentImport.status_message}</p>
          </Card.Section>
          <Card.Section title={t("import.show.importLog")}>
            {/* only show failed location */}
            <ExceptionList items={locationImportItems} />
          </Card.Section>
        </Card>
      </Layout.Section>
      <Layout.Section fullWidth>{completedPageActions}</Layout.Section>
    </>
  );

  return (
    <Page>
      <TitleBar
        title={t("import.show.title")}
        breadcrumbs={[{ content: t("import.breadcrumbs.locations"), url: "/places/locations" }]}
      />
      <Layout>
        {alreadyCompleted
          ? currentImport.success
            ? successMarkup
            : failureMarkup
          : inProgressMarkup}
      </Layout>
    </Page>
  );
}
